import { memo } from 'react';

import { Button } from '../../designSystem/Button';
import { cn } from '../../lib/styles';

export interface SectionPlaceholderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  onActionClick?: () => void;
  actionSubtitle?: React.ReactNode;
  fullScreen?: boolean;
}

const SectionPlaceholderBase = ({
  icon,
  title,
  description,
  action,
  onActionClick,
  actionSubtitle,
  fullScreen,
  className,
  ...rest
}: SectionPlaceholderProps) => {
  const renderAction = () => {
    if (!action && typeof onActionClick !== 'function') return null;

    const content =
      typeof action === 'object' ? (
        action
      ) : (
        <Button size="lg" className="w-full" onClick={onActionClick}>
          {action}
        </Button>
      );

    return (
      <div className="flex flex-col items-center gap-6 w-full">
        {content}

        {actionSubtitle ? <div className="text-text-primary font-body-md">{actionSubtitle}</div> : null}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-12 items-center justify-center px-16 py-14',
        { 'h-full w-full': fullScreen },
        className,
      )}
      {...rest}
    >
      <div className="flex flex-col gap-2 items-center">
        {icon ? <div className="flex items-center justify-center">{icon}</div> : null}

        {title ? (
          <div className="text-text-primary font-heading-xl font-semibold text-center dark:text-white">{title}</div>
        ) : null}

        {description ? (
          <div
            className="text-text-primary font-body-md font-medium text-center dark:text-white/80"
            style={{ textTransform: 'none', fontWeight: 400 }}
          >
            {description}
          </div>
        ) : null}
      </div>

      {renderAction()}
    </div>
  );
};

export const SectionPlaceholder = memo(SectionPlaceholderBase);
